import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Stack,
} from 'component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetPlotLabelNames, usePostPlotLabelName, usePostPlotLabelValue } from '@/api/rest/resources/organization';
import { PlotLabelValue } from '@/api/rest/resources/types/plotGroup';

import { AutocompleteCreateField } from './AutocompleteCreateField';

type AddNewLabelDialogProps = {
  open: boolean;
  organizationId: string;
  existingLabelValues: PlotLabelValue[];
  onClose: () => void;
  onCreated: (label: PlotLabelValue) => void;
};

export const AddNewLabelDialog = ({
  open,
  organizationId,
  existingLabelValues,
  onClose,
  onCreated,
}: AddNewLabelDialogProps) => {
  const { t } = useTranslation();
  const [labelName, setLabelName] = useState('');
  const [error, setError] = useState<string | undefined>();

  const { data: labelNames } = useGetPlotLabelNames({ queryParams: { organization_id: organizationId } });
  const { mutateAsync: createLabelName, isPending: isCreatingLabelName } = usePostPlotLabelName();
  const { mutateAsync: createLabelValue, isPending: isCreatingLabelValue } = usePostPlotLabelValue();
  const isPending = isCreatingLabelName || isCreatingLabelValue;

  const handleClose = () => {
    setLabelName('');
    setError(undefined);
    onClose();
  };

  const handleSelectExisting = (existingValue: PlotLabelValue) => {
    setLabelName('');
    onCreated(existingValue);
  };

  const handleCreateNew = async () => {
    const name = labelName.trim();
    if (!name) return;
    setError(undefined);
    try {
      const existingLabelName = labelNames.find((n) => n.name.toLowerCase() === name.toLowerCase());
      const labelNameId = existingLabelName
        ? existingLabelName.id
        : (await createLabelName({ bodyData: { name, organization: organizationId } })).id;
      const created = await createLabelValue({
        pathVariables: { plot_label_name_id: labelNameId },
        bodyData: { value: name },
      });
      setLabelName('');
      onCreated(created);
    } catch {
      setError(t('shared.uploadPlots.addLabelDialog.saveFailed'));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) handleClose();
      }}
    >
      <DialogContent>
        <DialogHeader className='typography-h2 w-full text-success md:typography-h3'>
          <DialogTitle>{t('shared.uploadPlots.addLabelDialog.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stack spacing={2}>
            <label className='typography-overline text-text-secondary'>
              {t('shared.uploadPlots.addLabelDialog.fieldLabel')}
            </label>

            <AutocompleteCreateField
              items={existingLabelValues}
              getItem={(item) => ({ key: item.id, label: item.value, meta: item.label_name })}
              searchValue={labelName}
              onSearchValueChange={setLabelName}
              onSelectItem={handleSelectExisting}
              onCreateNew={handleCreateNew}
              disabled={isPending}
              placeholder={t('shared.uploadPlots.addLabelDialog.placeholder')}
              noResultsLabel={t('shared.uploadPlots.addLabelDialog.noResults')}
              createOptionLabel={(name) => t('shared.uploadPlots.createOption', { name })}
              inputClassName='w-full rounded-lg border border-neutral-black-23 px-3 py-2 text-sm outline-none focus:border-primary-100'
            />

            {error && <span className='typography-caption text-error'>{error}</span>}
          </Stack>
        </DialogBody>
        <DialogFooter className='pt-4 flex-row justify-end'>
          <Button variant='outline' onClick={handleClose}>
            {t('global.ui.buttons.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
